import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Trophy, Medal, Award, Crown, Star, TrendingUp } from 'lucide-react';

export default function Leaderboard({ user }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard`);
      setLeaderboard(res.data);
    } catch (error) {
      console.error('Failed to load leaderboard', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (index) => {
    if (index === 0) {
      return {
        icon: <Crown className="text-yellow-500" size={32} />,
        badge: '🥇',
        gradient: 'from-yellow-400 via-yellow-500 to-amber-500',
        ring: 'ring-yellow-400',
        glow: 'shadow-yellow-300/50'
      };
    }
    if (index === 1) {
      return {
        icon: <Trophy className="text-gray-400" size={28} />,
        badge: '🥈',
        gradient: 'from-gray-300 via-gray-400 to-gray-500',
        ring: 'ring-gray-400',
        glow: 'shadow-gray-300/50'
      };
    }
    if (index === 2) {
      return {
        icon: <Medal className="text-orange-600" size={28} />,
        badge: '🥉',
        gradient: 'from-orange-400 via-orange-500 to-orange-600',
        ring: 'ring-orange-400',
        glow: 'shadow-orange-300/50'
      };
    }
    return {
      icon: <span className="text-lg font-bold text-gray-600">#{index + 1}</span>,
      badge: null,
      gradient: 'from-gray-100 to-gray-200',
      ring: '',
      glow: ''
    };
  };

  const getAvatarFrame = (index) => {
    if (index === 0) return 'ring-4 ring-yellow-400 shadow-lg shadow-yellow-300/50';
    if (index === 1) return 'ring-4 ring-gray-400 shadow-lg shadow-gray-300/50';
    if (index === 2) return 'ring-4 ring-orange-400 shadow-lg shadow-orange-300/50';
    return 'ring-2 ring-gray-300';
  };

  return (
    <div className="p-4 md:p-8 pt-20 md:pt-8" data-testid="leaderboard-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
              🏆 排行榜 🏆
            </span>
          </h1>
          <p className="text-gray-600 text-lg">看看誰是今天的冠軍！</p>
        </div>

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="mb-12">
            <div className="flex items-end justify-center gap-4 mb-8">
              {/* 2nd Place */}
              <div className="flex flex-col items-center w-1/3 transform hover:scale-105 transition-transform duration-300">
                <div className={`relative mb-4 ${getAvatarFrame(1)}`}>
                  <img 
                    src={leaderboard[1].avatar_url?.startsWith('http') 
                      ? leaderboard[1].avatar_url 
                      : leaderboard[1].avatar_url 
                        ? `${window.location.origin}/api/media-files${leaderboard[1].avatar_url.replace('/media', '')}` 
                        : 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
                    alt={leaderboard[1].username} 
                    className="w-24 h-24 rounded-full object-cover bg-gray-100"
                  />
                  <div className="absolute -top-2 -right-2 text-3xl animate-bounce">
                    🥈
                  </div>
                </div>
                <div className={`bg-gradient-to-b ${getRankBadge(1).gradient} text-white rounded-t-2xl shadow-lg p-4 w-full text-center h-32 flex flex-col justify-center`}>
                  <div className="font-bold text-lg truncate">{leaderboard[1].username}</div>
                  <div className="text-3xl font-bold mt-2">{leaderboard[1].points}</div>
                  <div className="text-sm opacity-90">積分</div>
                </div>
              </div>

              {/* 1st Place */}
              <div className="flex flex-col items-center w-1/3 transform hover:scale-110 transition-transform duration-300 relative">
                <div className="absolute -top-4 animate-bounce">
                  <Crown className="text-yellow-400" size={40} />
                </div>
                <div className={`relative mb-4 mt-8 ${getAvatarFrame(0)}`}>
                  <img 
                    src={leaderboard[0].avatar_url?.startsWith('http') 
                      ? leaderboard[0].avatar_url 
                      : leaderboard[0].avatar_url 
                        ? `${window.location.origin}/api/media-files${leaderboard[0].avatar_url.replace('/media', '')}` 
                        : 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
                    alt={leaderboard[0].username} 
                    className="w-32 h-32 rounded-full object-cover bg-gray-100"
                  />
                  <div className="absolute -top-2 -right-2 text-4xl animate-pulse">
                    🥇
                  </div>
                </div>
                <div className={`bg-gradient-to-b ${getRankBadge(0).gradient} text-white rounded-t-2xl shadow-2xl p-6 w-full text-center h-40 flex flex-col justify-center`}>
                  <div className="font-bold text-xl truncate">{leaderboard[0].username}</div>
                  <div className="text-4xl font-bold mt-2">{leaderboard[0].points}</div>
                  <div className="text-sm opacity-90">積分</div>
                </div>
              </div>

              {/* 3rd Place */}
              <div className="flex flex-col items-center w-1/3 transform hover:scale-105 transition-transform duration-300">
                <div className={`relative mb-4 ${getAvatarFrame(2)}`}>
                  <img 
                    src={leaderboard[2].avatar_url?.startsWith('http') 
                      ? leaderboard[2].avatar_url 
                      : leaderboard[2].avatar_url 
                        ? `${window.location.origin}/api/media-files${leaderboard[2].avatar_url.replace('/media', '')}` 
                        : 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
                    alt={leaderboard[2].username} 
                    className="w-24 h-24 rounded-full object-cover bg-gray-100"
                  />
                  <div className="absolute -top-2 -right-2 text-3xl animate-bounce" style={{ animationDelay: '0.2s' }}>
                    🥉
                  </div>
                </div>
                <div className={`bg-gradient-to-b ${getRankBadge(2).gradient} text-white rounded-t-2xl shadow-lg p-4 w-full text-center h-28 flex flex-col justify-center`}>
                  <div className="font-bold text-lg truncate">{leaderboard[2].username}</div>
                  <div className="text-3xl font-bold mt-2">{leaderboard[2].points}</div>
                  <div className="text-sm opacity-90">積分</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Full Leaderboard List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-500"></div>
              <p className="mt-4 text-gray-600">載入中...</p>
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="p-12 text-center">
              <Trophy className="mx-auto text-gray-300 mb-4" size={64} />
              <p className="text-gray-500 text-lg">暫無排名數據</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {leaderboard.map((student, index) => {
                const rankInfo = getRankBadge(index);
                const isCurrentUser = student.user_id === user.user_id;
                
                return (
                  <div 
                    key={student.user_id} 
                    className={`p-5 flex items-center gap-4 transition-all duration-300 ${
                      isCurrentUser 
                        ? 'bg-gradient-to-r from-orange-50 to-pink-50 border-l-4 border-orange-500' 
                        : 'hover:bg-gray-50'
                    } ${index < 3 ? 'bg-gradient-to-r ' + rankInfo.gradient.replace('from-', 'from-').replace('to-', 'to-') + '/10' : ''}`}
                    data-testid="leaderboard-item"
                    style={{
                      animation: `slideIn 0.5s ease-out ${index * 50}ms forwards`,
                      opacity: 0
                    }}
                  >
                    {/* Rank Badge */}
                    <div className={`w-16 flex items-center justify-center ${index < 3 ? 'transform scale-110' : ''}`}>
                      {rankInfo.icon}
                    </div>
                    
                    {/* Avatar with Frame */}
                    <div className={`relative ${getAvatarFrame(index)}`}>
                      <img 
                        src={student.avatar_url?.startsWith('http') 
                          ? student.avatar_url 
                          : student.avatar_url 
                            ? `${window.location.origin}/api/media-files${student.avatar_url.replace('/media', '')}` 
                            : 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'}
                        alt={student.username} 
                        className="w-14 h-14 rounded-full object-cover bg-gray-100"
                      />
                      {index < 3 && (
                        <div className="absolute -bottom-1 -right-1 text-xl">
                          {rankInfo.badge}
                        </div>
                      )}
                    </div>
                    
                    {/* Student Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-lg text-gray-800">{student.username}</div>
                        {isCurrentUser && (
                          <span className="px-2 py-1 bg-orange-500 text-white text-xs rounded-full font-semibold animate-pulse">
                            你
                          </span>
                        )}
                        {index === 0 && (
                          <Star className="text-yellow-500 fill-yellow-500 animate-pulse" size={20} />
                        )}
                      </div>
                      {student.student_id && (
                        <div className="text-sm text-gray-500">{student.student_id}</div>
                      )}
                    </div>
                    
                    {/* Points Display */}
                    <div className="text-right">
                      <div className={`text-3xl font-bold ${
                        index === 0 ? 'bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent' :
                        index === 1 ? 'text-gray-600' :
                        index === 2 ? 'text-orange-600' :
                        'text-orange-500'
                      }`}>
                        {student.points}
                      </div>
                      <div className="text-sm text-gray-600 font-medium">積分</div>
                    </div>

                    {/* Trending Icon for top performers */}
                    {index < 5 && (
                      <div>
                        <TrendingUp className={`${
                          index === 0 ? 'text-yellow-500' :
                          index === 1 ? 'text-gray-500' :
                          index === 2 ? 'text-orange-500' :
                          'text-green-500'
                        }`} size={24} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Motivational Footer */}
        {leaderboard.length > 0 && (
          <div className="mt-8 text-center p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200">
            <p className="text-lg font-semibold text-gray-700 mb-2">
              💪 繼續努力，爭取更高的排名！
            </p>
            <p className="text-sm text-gray-600">
              完成每日任務和課堂紀錄可以獲得更多積分
            </p>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
